import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table2, Calendar, ArrowUp, ArrowDown } from "lucide-react";
import axiosInstance from "../../axiosInstance";
import GeneralTable from "../../components/Table/GeneralTable";
import StatusCard from "../../components/StatusCard/StatusCard";
import ReusableCalendar from "../../components/AttendanceCalender/ReusableCalendar";
import SummaryCard from "../../components/ui/SummaryCard";
import StatusBadge from "../../components/ui/StatusBadge";
import {
  StatusTabs,
  SelectDropdown,
  FilterToolbar,
} from "../../components/ui/FilterControls";
import { ErrorState } from "../../components/ui/Feedback";
import { parseLocalDate, formatDateLong } from "./leaveUtils";
// Legacy stylesheet: other pages may still rely on its global selectors
// (.status-badge, .view-toggle, ...). Left untouched on purpose.
import "./MyLogs.scss";
import "./MyLogsPage.scss";

// Statuses that should be grouped into the "Rejected / Cancelled" bucket
const REJECTED_STATUSES = ["rejected", "cancelled", "canceled"];

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "requested", label: "Requested" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected / Cancelled" },
];

const SORT_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "type", label: "Status" },
];

const toTitleCase = (text) => text.replace(/\b\w/g, (c) => c.toUpperCase());

// Single source of truth for reading an attendance record's punch-out status.
// Handles the punchoutStatus / punchOutStatus casing inconsistency and
// normalizes to lowercase so comparisons are consistent everywhere.
const getStatus = (item) =>
  (item.punchoutStatus || item.punchOutStatus || "unknown").toLowerCase();

const formatDate = (dateString) => {
  const date = parseLocalDate(dateString);
  return date ? formatDateLong(date) : "N/A";
};

// Minutes between "09:30 am" and "06:15 pm" (wraps past midnight), or null.
const workingMinutes = (punchIn, punchOut) => {
  if (!punchIn || !punchOut) return null;

  const timeRegex = /(\d{1,2}):(\d{2})\s*(am|pm)/i;
  const inMatch = punchIn.match(timeRegex);
  const outMatch = punchOut.match(timeRegex);
  if (!inMatch || !outMatch) return null;

  const toMinutes = (hours, minutes, period) => {
    let hour = parseInt(hours, 10);
    if (period.toLowerCase() === "pm" && hour !== 12) hour += 12;
    if (period.toLowerCase() === "am" && hour === 12) hour = 0;
    return hour * 60 + parseInt(minutes, 10);
  };

  let diff =
    toMinutes(outMatch[1], outMatch[2], outMatch[3]) -
    toMinutes(inMatch[1], inMatch[2], inMatch[3]);
  if (diff < 0) diff += 24 * 60;
  return diff;
};

const formatMinutes = (minutes) =>
  minutes === null ? "N/A" : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

const TimeCell = ({ time, note }) => (
  <div className="ml-time">
    <span className="ml-time__value">{time || "N/A"}</span>
    {note && <span className="ml-time__note">{note}</span>}
  </div>
);

function MyLogs() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'calendar'

  const fetchAttendanceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/All`,
      );

      if (response.status !== 200) {
        throw new Error("Failed to fetch attendance data.");
      }

      setAttendanceData(response.data.data || []);
    } catch (err) {
      setError("Error fetching attendance data. Please try again later.");
      console.error("Attendance fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  // Counts that describe the whole data set (they feed the cards and tabs)
  const counts = useMemo(() => {
    const result = {
      all: attendanceData.length,
      requested: 0,
      approved: 0,
      rejected: 0,
    };
    attendanceData.forEach((item) => {
      const status = getStatus(item);
      if (status === "requested") result.requested += 1;
      else if (status === "approved") result.approved += 1;
      else if (REJECTED_STATUSES.includes(status)) result.rejected += 1;
    });
    return result;
  }, [attendanceData]);

  const rows = useMemo(() => {
    let filtered = attendanceData;
    if (filter !== "all") {
      filtered = attendanceData.filter((item) => {
        const status = getStatus(item);
        return filter === "rejected"
          ? REJECTED_STATUSES.includes(status)
          : status === filter;
      });
    }

    const direction = sortOrder === "asc" ? 1 : -1;
    const sorted = filtered
      .map((item, index) => {
        const date = parseLocalDate(item.attendanceDate);
        return {
          item,
          index,
          key:
            sortBy === "date"
              ? date
                ? date.getTime()
                : 0
              : item.attendanceType || "",
        };
      })
      .sort((a, b) => {
        if (a.key === b.key) return a.index - b.index;
        return (a.key > b.key ? 1 : -1) * direction;
      })
      .map((entry) => entry.item);

    return sorted.map((attendance) => {
      const date = parseLocalDate(attendance.attendanceDate);
      const minutes = workingMinutes(
        attendance.punchInTime,
        attendance.punchOutTime,
      );
      return {
        date: formatDate(attendance.attendanceDate),
        dateValue: date ? date.getTime() : null,
        attendanceType: attendance.attendanceType || "",
        punchInTime: attendance.punchInTime || "",
        punchInMessage: attendance.punchInMessage || "",
        punchOutTime: attendance.punchOutTime || "",
        punchOutMessage: attendance.punchOutMessage || "",
        punchOutStatus: toTitleCase(getStatus(attendance)),
        workingHours: formatMinutes(minutes),
        workingMinutes: minutes,
        notes: attendance.managerRequestMessage || "",
      };
    });
  }, [attendanceData, filter, sortBy, sortOrder]);

  const isDefaultView =
    filter === "all" && sortBy === "date" && sortOrder === "desc";

  const resetView = () => {
    setFilter("all");
    setSortBy("date");
    setSortOrder("desc");
  };

  const tabs = STATUS_TABS.map((tab) => ({ ...tab, count: counts[tab.key] }));

  const columns = [
    {
      header: "Date",
      accessor: "date",
      sortAccessor: (row) => row.dateValue,
    },
    {
      header: "Status",
      accessor: "attendanceType",
      render: (row) =>
        row.attendanceType ? (
          <StatusBadge status={row.attendanceType} pendingLabel="Requested" />
        ) : (
          <span className="hr-muted">Unknown</span>
        ),
    },
    {
      header: "Punch In",
      accessor: "punchInTime",
      render: (row) => (
        <TimeCell time={row.punchInTime} note={row.punchInMessage} />
      ),
    },
    {
      header: "Punch Out",
      accessor: "punchOutTime",
      render: (row) => (
        <TimeCell time={row.punchOutTime} note={row.punchOutMessage} />
      ),
    },
    {
      header: "Punch Out Status",
      accessor: "punchOutStatus",
      render: (row) => <StatusCard status={row.punchOutStatus} />,
    },
    {
      header: "Working Hours",
      accessor: "workingHours",
      sortAccessor: (row) => row.workingMinutes,
    },
    {
      header: "Notes",
      accessor: "notes",
      render: (row) =>
        row.notes ? (
          <span className="ml-note" title={row.notes}>
            <strong>Manager:</strong> {row.notes}
          </span>
        ) : (
          <span className="hr-muted">—</span>
        ),
    },
  ];

  const viewToggle = (
    <div className="ml-view-toggle" role="group" aria-label="View mode">
      <button
        type="button"
        className={`ml-view-toggle__btn ${viewMode === "table" ? "is-active" : ""}`}
        aria-pressed={viewMode === "table"}
        onClick={() => setViewMode("table")}
      >
        <Table2 size={16} aria-hidden="true" />
        Table
      </button>
      <button
        type="button"
        className={`ml-view-toggle__btn ${viewMode === "calendar" ? "is-active" : ""}`}
        aria-pressed={viewMode === "calendar"}
        onClick={() => setViewMode("calendar")}
      >
        <Calendar size={16} aria-hidden="true" />
        Calendar
      </button>
    </div>
  );

  return (
    <div className="my-logs-page">
      {error ? (
        <section className="hr-card">
          {viewToggle}
          <ErrorState
            title="Couldn't load your attendance"
            message={error}
            onRetry={fetchAttendanceData}
          />
        </section>
      ) : viewMode === "table" ? (
        <>
          <div className="hr-summary-grid">
            <SummaryCard
              tone="primary"
              icon="calendar_month"
              label="Total Days"
              value={counts.all}
              hint="Attendance records"
              loading={loading}
            />
            <SummaryCard
              tone="warning"
              icon="pending_actions"
              label="Requested"
              value={counts.requested}
              hint="Awaiting approval"
              loading={loading}
            />
            <SummaryCard
              tone="success"
              icon="task_alt"
              label="Approved"
              value={counts.approved}
              hint="Approved records"
              loading={loading}
            />
            <SummaryCard
              tone="danger"
              icon="event_busy"
              label="Rejected / Cancelled"
              value={counts.rejected}
              hint="Not accepted"
              loading={loading}
            />
          </div>

          <FilterToolbar
            tabs={
              <StatusTabs
                tabs={tabs}
                value={filter}
                onChange={setFilter}
                ariaLabel="Filter by status"
              />
            }
            onReset={resetView}
            resetDisabled={isDefaultView}
          >
            {viewToggle}
            <SelectDropdown
              id="logs-sort-by"
              label="Sort by"
              value={sortBy}
              onChange={setSortBy}
              options={SORT_OPTIONS}
            />
            <div className="hr-field">
              <span className="hr-field__label">Order</span>
              <button
                type="button"
                className="hr-btn hr-btn--secondary ml-order-btn"
                onClick={() =>
                  setSortOrder((order) => (order === "asc" ? "desc" : "asc"))
                }
              >
                {sortOrder === "asc" ? (
                  <ArrowUp size={15} aria-hidden="true" />
                ) : (
                  <ArrowDown size={15} aria-hidden="true" />
                )}
                {sortOrder === "asc" ? "Ascending" : "Descending"}
              </button>
            </div>
          </FilterToolbar>

          <GeneralTable
            columns={columns}
            data={rows}
            loading={loading}
            title="Attendance Records"
            emptyStateTitle="No attendance records"
            emptyStateSubtitle={
              filter === "all"
                ? "Your attendance will appear here once you start punching in."
                : "Nothing matches this status. Try another tab."
            }
          />
        </>
      ) : (
        /* Calendar view */
        <ReusableCalendar
          attendanceData={attendanceData}
          mode="single"
          employeeName="My"
        />
      )}
    </div>
  );
}

export default MyLogs;
